import { useQuery } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { api } from '../../api/client.ts';
import type { ProviderId } from '../../api/types.ts';
import { t } from '../../i18n/index.ts';
import { labels } from '../../i18n/labels.ts';

export interface ProviderInfo {
  id: ProviderId;
  label: string;
}

const usePublicAuth = <T,>(select: (d: { providers: ProviderInfo[]; inviteOnly?: boolean }) => T) =>
  useQuery({
    queryKey: ['auth', 'providers'],
    queryFn: () => api.get<{ providers: ProviderInfo[]; inviteOnly?: boolean }>('/api/auth/providers'),
    staleTime: 5 * 60_000,
    select,
  });

export const useProviders = () => usePublicAuth((d) => d.providers);

/** Si en esta instalación a los árboles se entra solo por invitación (no se pueden crear árboles nuevos). */
export const useInviteOnly = () => usePublicAuth((d) => !!d.inviteOnly).data ?? false;

/** Mensajes para los errores que el servidor devuelve en `?error=` tras volver del proveedor. */
export const OAUTH_ERRORS = labels<string>({
  oauth_failed: 'No pudimos completar el acceso. Inténtalo de nuevo.',
  oauth_cancelled: 'Se canceló el acceso con el proveedor.',
  oauth_unavailable: 'El proveedor no responde ahora. Inténtalo en unos minutos o entra con tu correo.',
  oauth_email_unverified:
    'Tu proveedor no confirma que ese correo sea tuyo. Verifícalo allí, o entra con tu correo y contraseña y vincula la cuenta desde «Mi cuenta».',
  oauth_no_email: 'Tu proveedor no compartió tu correo. Permite el acceso al correo e inténtalo de nuevo.',
  oauth_in_use: 'Esa cuenta ya está vinculada a otro usuario de Gallarday.',
  oauth_already_linked: 'Ya tienes vinculada otra cuenta de ese proveedor. Desvincúlala primero.',
  oauth_session: 'Tu sesión terminó. Entra de nuevo para vincular la cuenta.',
});

export const startUrl = (id: ProviderId, opts: { next?: string | null; link?: boolean } = {}) => {
  const qs = new URLSearchParams();
  if (opts.next) qs.set('next', opts.next);
  if (opts.link) qs.set('link', '1');
  const q = qs.toString();
  return `/api/auth/oauth/${id}/start${q ? `?${q}` : ''}`;
};

export function ProviderMark({ id }: { id: ProviderId }) {
  if (id === 'google') {
    // Marca "G" de Google en sus colores (uso permitido en botones de acceso).
    return (
      <svg viewBox="0 0 48 48" className="size-5" aria-hidden>
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
      </svg>
    );
  }
  return <KeyRound className="size-5 text-indigo" aria-hidden />;
}

/** Botones "Continuar con …": navegación completa al servidor, que redirige al proveedor. */
export function ProviderButtons({ next }: { next?: string | null }) {
  const providers = useProviders().data ?? [];
  if (!providers.length) return null;
  return (
    <div className="flex flex-col gap-3">
      {providers.map((p) => (
        <a
          key={p.id}
          href={startUrl(p.id, { next })}
          className="inline-flex h-12 items-center justify-center gap-3 rounded-full border border-line bg-surface px-6 text-base font-medium text-ink transition-colors hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo"
        >
          <ProviderMark id={p.id} /> {t('Continuar con {provider}', { provider: p.label })}
        </a>
      ))}
      <div className="my-2 flex items-center gap-3 text-[13.5px] text-ink-faint" role="separator">
        <span className="h-px flex-1 bg-line" /> {t('o con tu correo')} <span className="h-px flex-1 bg-line" />
      </div>
    </div>
  );
}
